import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import Fade from "react-reveal/Fade";
import { Link as LinkR } from "react-router-dom";

import Codebox from "../../videos/codebox.mp4";
import AboutMeIntro from "../AboutMeIntro";

// Set to false to restore the previous hero copy instantly
const USE_NEW_HERO = true;

const typeWriter = keyframes`
  0%, 10% { width: 0; }
  40%, 60% { width: 100%; }
  90%, 100% { width: 0; }
`;

const blinkCaret = keyframes`
  from, to { border-color: transparent }
  50% { border-color: #45fe47; }
`;

const HeroContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-height: 100vh;
  width: 100%;
  position: relative;
  z-index: 1;
  overflow: hidden;
  background: transparent;

  :before {
    content: "";
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%);
    z-index: 2;
  }
`;

const HeroBg = styled.div`
  position: absolute;
  top: 0; right: 0; bottom: 0; left: 0;
  z-index: 0;
`;

const VideoBg = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.6;
`;

const HeroContent = styled.div`
  z-index: 3;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px 20px 40px;
`;

const TypingWrapper = styled.div`
  height: 2.5rem;
  margin-bottom: 0.5rem;
  display: flex;
  justify-content: center;
  align-items: flex-end;
`;

const CarouselText = styled.h2`
  font-family: "VT323", monospace;
  font-weight: 400;
  color: #45fe47;
  font-size: 1.8rem;
  text-transform: uppercase;
  letter-spacing: 4px;
  margin: 0;
  overflow: hidden;
  white-space: nowrap;
  border-right: 4px solid #45fe47;
  display: inline-block;
  animation:
    ${typeWriter} ${(props) => props.duration}s steps(${(props) => props.length}, end) infinite,
    ${blinkCaret} 0.75s step-end infinite;

  @media screen and (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const BrandTitle = styled.h1`
  font-family: "VT323", monospace;
  font-weight: 900;
  color: #fff;
  font-size: clamp(3rem, 8vw, 5.2rem);
  text-transform: uppercase;
  letter-spacing: 3px;
  margin: 10px 0 6px;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
`;

const Headline = styled.p`
  font-family: "VT323", monospace;
  font-weight: 400;
  color: #45fe47;
  font-size: clamp(1.35rem, 3.2vw, 2rem);
  letter-spacing: 2px;
  margin: 0 0 12px;
  text-transform: uppercase;
`;

const MainTitle = styled.h1`
  font-family: "VT323", monospace;
  font-weight: 900;
  color: #fff;
  font-size: 5.2rem;
  text-transform: uppercase;
  letter-spacing: 3px;
  margin: 10px 0;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);

  @media screen and (max-width: 768px) {
    font-size: 2.8rem;
  }
`;

const Subheading = styled.p`
  font-family: "VT323", monospace;
  color: #888;
  font-size: 1.1rem;
  max-width: 600px;
  line-height: 1.4;
  margin-top: 15px;
  letter-spacing: 1px;

  @media screen and (max-width: 480px) {
    font-size: 0.9rem;
    max-width: 320px;
  }
`;

const CtaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-top: 28px;
`;

const CtaPrimary = styled(LinkR)`
  font-family: "VT323", monospace;
  font-size: 1.2rem;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 12px 28px;
  background: #45fe47;
  color: #000;
  border: 1px solid #45fe47;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: #000;
    color: #45fe47;
  }
`;

const CtaSecondary = styled.a`
  font-family: "VT323", monospace;
  font-size: 1.2rem;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 12px 28px;
  background: transparent;
  color: #45fe47;
  border: 1px solid #45fe47;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: #45fe47;
    color: #000;
  }
`;

const wordsNew = [
  "HomeschoolGuide.AI",
  "Archetype Systems",
  "Client PWAs",
  "Small Business Bots",
  "Learning Tools",
  "Practical AI Builds",
];

const wordsOld = [
  "Behavioral Design",
  "Decision Systems",
  "Product Strategy",
  "Adaptive Learning",
  "Identity Frameworks",
  "Human-Centered Technology",
];

const HeroSection = () => {
  const words = USE_NEW_HERO ? wordsNew : wordsOld;
  const [index, setIndex] = useState(0);
  const duration = 5;

  useEffect(() => {
    setIndex(0);
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % words.length);
    }, duration * 1000);
    return () => clearInterval(interval);
  }, [words.length]);

  return (
    <HeroContainer id="home">
      <HeroBg>
        <VideoBg playsInline autoPlay loop muted src={Codebox} />
      </HeroBg>

      <AboutMeIntro />

      <HeroContent>
        <Fade top>
          <TypingWrapper>
            <CarouselText
              key={words[index]}
              length={words[index].length}
              duration={duration}
            >
              {words[index]}
            </CarouselText>
          </TypingWrapper>
        </Fade>

        {USE_NEW_HERO ? (
          <>
            <Fade bottom duration={1000} delay={300}>
              <BrandTitle>Studio Voice2fly</BrandTitle>
              <Headline>Practical AI, built human-first</Headline>
            </Fade>

            <Fade bottom duration={1500} delay={600}>
              <Subheading>
                Flagship startup, archetype systems, and client-ready tools —
                learning aids, PWAs, and practical AI you can try or ship.
              </Subheading>
              <CtaRow>
                <CtaPrimary to="/templates">Browse portfolio</CtaPrimary>
                <CtaSecondary
                  href="//cal.com/james-studiovoice2fly.com/ai-strategy-session"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Work with me
                </CtaSecondary>
              </CtaRow>
            </Fade>
          </>
        ) : (
          <>
            <Fade bottom duration={1000} delay={300}>
              <MainTitle>Human-Centered Product Factory</MainTitle>
            </Fade>

            <Fade bottom duration={1500} delay={600}>
              <Subheading>
                Combining psychology, systems thinking, and technology to create
                products people actually use.
              </Subheading>
            </Fade>
          </>
        )}
      </HeroContent>
    </HeroContainer>
  );
};

export default HeroSection;
